import React from 'react'

import { Box, Grid, Tooltip, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { dsColorNew } from "@palette"
import { oneWayData, oneWayPriceMode } from "Pages/AnalyzersV2/OneWay/@signals"

export const NO_FEE = 'N/A'

export default function feesColumn(date, lors, acrissCodes) {
	// once per date, not once per cell: the range is the same for every row of the column
	const range = getFeeRangePerColumn(date, lors, acrissCodes)

	return {
		name: date,
		setBodyCellProps: () => ({ sx: { p: 0, verticalAlign: 'top', userSelect: 'none', cursor: 'default', borderLeft: `2px solid ${dsColorNew.grayBorder}` } }),
		render: (value, row) => <FeeCell date={date} lors={lors} acrissCodes={acrissCodes} value={value} range={range} isMain={!!row?.isMainRow} />
	}
}

/**
 * Cheapest and most expensive fee of each (lor, acriss) pair across every row of the table.
 * The cell only receives its own row, so the comparison is read back from the data signal —
 * which is what makes the green/red border mean "cheapest row of this column".
 */
function getFeeRangePerColumn(date, lors, acrissCodes) {
	const min = {}
	const max = {}

	;(oneWayData.value?.results || []).forEach(row => {
		lors.forEach(lor => {
			const entries = row?.[date]?.[String(lor)] || []
			entries.forEach(({ acriss, fee }) => {
				if (fee === NO_FEE || fee == null || !acrissCodes.includes(acriss)) return

				const key = `${lor}-${acriss}`
				if (min[key] == null || fee < min[key]) min[key] = fee
				if (max[key] == null || fee > max[key]) max[key] = fee
			})
		})
	})

	return { min, max }
}

function FeeCell({ value, date, lors, acrissCodes, range, isMain }) {
	const pickupName = oneWayData.value?.pickupName ?? 'RT'
	const dropoffName = oneWayData.value?.dropoffName ?? 'OW'
	// the reference row: a web in Direct Sites, a brand in Broker
	const mainName = oneWayData.value?.mainName

	const { min, max } = range

	return <Box sx={{ p: 0.75 }}>
		<Grid container>
			{lors.map(lor => {
				const entries = value?.[String(lor)] || []

				return <Grid key={`${date}-${lor}`} item xs={12 / lors.length}>
					<Grid container>
						{acrissCodes.map(acriss => {
							const entry = entries.find(x => x.acriss === acriss)
							const fee = entry?.fee ?? NO_FEE
							const key = `${lor}-${acriss}`
							const hasRange = min[key] != null && min[key] !== max[key]

							return <Grid key={`${date}-${lor}-${acriss}`} item xs={12 / acrissCodes.length} sx={{ px: 0.25 }}>
								<FeeBox
									fee={fee}
									roundTripPrice={entry?.roundTripPrice}
									oneWayPrice={entry?.oneWayPrice}
									car={entry?.car}
									avgFee={entry?.avgFee}
									acriss={acriss}
									pickupName={pickupName}
									dropoffName={dropoffName}
									isMain={isMain}
									mainName={mainName}
									diff={entry?.diff}
									comparisonType={entry?.comparisonType}
									isCheapest={hasRange && fee !== NO_FEE && fee === min[key]}
									isMostExpensive={hasRange && fee !== NO_FEE && fee === max[key]}
								/>
							</Grid>
						})}
					</Grid>
				</Grid>
			})}
		</Grid>
	</Box>
}

function FeeBox({ fee, roundTripPrice, oneWayPrice, car, avgFee, acriss, pickupName, dropoffName, isMain, mainName, diff, comparisonType, isCheapest, isMostExpensive }) {
	const isNoFee = fee === NO_FEE

	// the analyzers' cell tones: green the cheapest of the column, red the dearest, blue the main row
	const borderColor = isNoFee ? dsColorNew.grayBorder
		: isCheapest ? alpha(dsColorNew.successBar, 0.55)
		: isMostExpensive ? dsColorNew.dangerBorder
		: isMain ? dsColorNew.noteInfoBorder
		: dsColorNew.grayBorder
	const backgroundColor = isNoFee ? 'transparent'
		: isCheapest ? dsColorNew.successBg
		: isMostExpensive ? dsColorNew.dangerBg
		: isMain ? dsColorNew.pillActiveBg
		: dsColorNew.white

	const displayValue = isNoFee
		? NO_FEE
		: oneWayPriceMode.value === 'roundTrip' ? oneWayPrice : fee

	// the gap is a gap between fees: with the round trip price on screen it would be comparing
	// one number against another one that is not shown
	const showDiff = diff != null && oneWayPriceMode.value !== 'roundTrip'
	const diffColor = comparisonType === 'more expensive' ? dsColorNew.successText
		: comparisonType === 'cheaper' ? dsColorNew.dangerRed
		: dsColorNew.textMuted
	const diffBg = comparisonType === 'more expensive' ? dsColorNew.successBg
		: comparisonType === 'cheaper' ? dsColorNew.dangerBg
		: dsColorNew.pillTrackBg
	// the tooltip is dark, so its gap takes the bright tones of the same two colours
	const tooltipDiffColor = comparisonType === 'more expensive' ? dsColorNew.successBar
		: comparisonType === 'cheaper' ? dsColorNew.dangerSoft
		: 'inherit'
	// two decimals are noise inside a cell this size; the exact value stays in the tooltip
	const diffLabel = showDiff ? `${diff > 0 ? '+' : ''}${Math.round(diff)}%` : null

	return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start', width: '100%' }}>
		<Tooltip
			arrow
			placement="top"
			title={isNoFee
				? 'No round trip price to compare against'
				: <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5 }}>
					<Typography variant="caption">Acriss: <b>{acriss}</b></Typography>
					{pickupName && <Typography variant="caption">{pickupName}-{pickupName}: <b>{roundTripPrice}</b></Typography>}
					{pickupName && <Typography variant="caption">{pickupName}-{dropoffName}: <b>{oneWayPrice}</b></Typography>}
					<Typography variant="caption">Fee: <b>{fee}</b></Typography>
					{avgFee != null && <Typography variant="caption">Avg fee: <b>{avgFee}</b></Typography>}
					{diff != null && <Typography variant="caption">
						Vs {mainName || 'main'}: <b style={{ color: tooltipDiffColor }}>{diff > 0 ? '+' : ''}{diff}%</b>
					</Typography>}
					{car && <Typography variant="caption">Car: <b>{car}</b></Typography>}
				</Box>}
		>
			<Box sx={{
				border: `1px ${isNoFee ? 'dashed' : 'solid'} ${borderColor}`,
				backgroundColor: backgroundColor,
				color: isNoFee ? dsColorNew.textSubtle : dsColorNew.textTitle,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				borderRadius: '8px',
				width: '100%',
				minHeight: 40,
				gap: 0.25,
				py: 0.5,
			}}>
				<Typography sx={{ textAlign: 'center', fontSize: 13, fontWeight: 700, lineHeight: 1.2, color: 'inherit' }}>
					{displayValue}
				</Typography>
				{diffLabel && <Box component="span" sx={{
					display: 'inline-flex', alignItems: 'center', height: 16, px: 0.75, borderRadius: '6px',
					fontSize: 10.5, fontWeight: 700, lineHeight: 1,
					backgroundColor: diffBg,
					color: diffColor,
				}}>
					{diffLabel}
				</Box>}
			</Box>
		</Tooltip>
	</Box>
}
